import { useEffect, useRef, useState } from 'react';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { applyChatsAcknowledgment, cancelChatsReads, invalidateChats, type ConversationReadState } from './chats';
import { queryClient, queryKeys } from './query-client';
import { applyUnreadNotificationReceipt, refreshUnreadNotifications } from './unread-notifications-store';
import { settingsKeys, type Recipient } from '../components/settings/shared/settings-queries';
import { beginChatRead, chatReadEpoch, notificationPrepareExpired, pendingChatReadCount, pendingNotificationTaps, settleChatRead } from './chat-session';
import { parseConversationReadState } from './chat-read-state';

export function currentChatOwner(): string | null {
  const recipient = queryClient.getQueryData<Recipient>(settingsKeys.recipient);
  return recipient?.email && recipient.metaSiteId ? JSON.stringify([recipient.email, recipient.metaSiteId]) : null;
}

export function clearPendingChatReads(): void { pendingNotificationTaps.clear(); }
export class ReadSnapshotExpiredError extends Error {
  constructor() { super('read_snapshot_expired'); this.name = 'ReadSnapshotExpiredError'; }
}
export class NotificationSnapshotUnsupportedError extends Error {
  constructor() { super('notification_snapshot_unsupported'); this.name = 'NotificationSnapshotUnsupportedError'; }
}

/** Explicit legacy bell action; never reuse it to widen a captured snapshot. */
export async function markLegacyNotificationsRead(owner: string, id?: string): Promise<void> {
  if (currentChatOwner() !== owner) throw new DOMException('Read operation no longer active', 'AbortError');
  const epoch = beginChatRead();
  clearTimeout(reconcileTimer);
  cancelChatsReads();
  void queryClient.cancelQueries({ queryKey: queryKeys.notifications });
  applyUnreadNotificationReceipt(null);
  try {
    const response = await apiFetch(`${API_BASE}/api/notifications`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'READ', ...(id ? { id } : {}) }),
    });
    if (!response.ok || (await response.json()).ok !== true) throw new Error('Notification update failed');
  } finally {
    settleChatRead(epoch);
    if (currentChatOwner() === owner) scheduleReconciliation();
  }
}
export async function acknowledgeNotificationTap(owner: string, id: string, date: string | null): Promise<void> {
  const key = JSON.stringify([owner, id, date]);
  const operation = pendingNotificationTaps.get(key) ?? { requestId: crypto.randomUUID(), createdAt: Date.now() };
  pendingNotificationTaps.set(key, operation);
  const active = () => currentChatOwner() === owner;
  if (!active()) return;
  const mayUseLegacy = !operation.prepareAttempted;
  try {
  if (!operation.snapshotToken) {
    if (notificationPrepareExpired(operation.createdAt)) throw new ReadSnapshotExpiredError();
    operation.prepareAttempted = true;
    const snapshot = await postChatRead<{ snapshotToken: string }>({ action: 'notification-read-prepare', requestId: operation.requestId, notificationId: id });
    if (notificationPrepareExpired(operation.createdAt)) throw new ReadSnapshotExpiredError();
    if (!snapshot.snapshotToken) throw new Error('Missing notification snapshot');
    operation.snapshotToken = snapshot.snapshotToken;
  }
  if (!active()) return;
  const receipt = await acknowledgeAllNotifications(operation.snapshotToken, active);
  if (receipt.complete) pendingNotificationTaps.delete(key);
  else throw new Error('Notification read is incomplete');
  } catch (error) {
    if (error instanceof NotificationSnapshotUnsupportedError && !operation.snapshotToken && active()) {
      pendingNotificationTaps.delete(key);
      // A previous lost response may already have frozen membership. Retire it;
      // only another explicit selection may use current legacy membership.
      if (!mayUseLegacy || notificationPrepareExpired(operation.createdAt)) throw new ReadSnapshotExpiredError();
      await markLegacyNotificationsRead(owner, id);
      return;
    }
    if (error instanceof ReadSnapshotExpiredError && pendingNotificationTaps.get(key) === operation) pendingNotificationTaps.delete(key);
    throw error;
  }
}

export interface NotificationReadReceipt {
  complete: boolean; operationId: string; acknowledgedPingNotificationIds: string[]; unreadNotificationCount: number | null;
  readMode?: 'VIEWER_ROWS' | null;
  selectionComplete?: boolean;
  acknowledgedViewerNotificationIds?: string[];
}
const RECEIPT_EPOCH = Symbol('receiptEpoch');
let reconcileTimer: ReturnType<typeof setTimeout> | undefined;
function scheduleReconciliation() {
  clearTimeout(reconcileTimer);
  reconcileTimer = setTimeout(() => {
    if (pendingChatReadCount() > 0) { scheduleReconciliation(); return; }
    invalidateChats();
    void queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
    refreshUnreadNotifications();
  }, 600);
}
function isCurrentReceipt(receipt: object): boolean {
  return (receipt as NotificationReadReceipt & { [RECEIPT_EPOCH]?: number })[RECEIPT_EPOCH] === chatReadEpoch();
}
interface ConversationReadReceipt { conversationId: string; readState: ConversationReadState; notifications: NotificationReadReceipt | null; asOf: string }
export async function postChatRead<T>(body: Record<string, string>): Promise<T> {
  const epoch = beginChatRead();
  clearTimeout(reconcileTimer);
  cancelChatsReads();
  void queryClient.cancelQueries({ queryKey: queryKeys.notifications });
  applyUnreadNotificationReceipt(null);
  let settled = false;
  try {
    const response = await apiFetch(`${API_BASE}/api/chat-reads`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    if (!response.ok) {
      if (response.status === 501) {
        const failure = await response.json().catch(() => null);
        if (failure?.error === 'notification_snapshot_unsupported') throw new NotificationSnapshotUnsupportedError();
      }
      if (response.status === 410) {
        const failure = await response.json().catch(() => null);
        if (failure?.error === 'read_snapshot_expired') throw new ReadSnapshotExpiredError();
      }
      throw new Error(`Read update failed (${response.status})`);
    }
    const result = await response.json();
    if (body.action === 'conversation-acknowledge') result.readState = parseConversationReadState(result.readState);
    const receipt = result.notifications ?? result;
    const receiptEpoch = settleChatRead(epoch);
    settled = true;
    cancelChatsReads();
    applyUnreadNotificationReceipt(null);
    if (receipt && typeof receipt === 'object') Object.defineProperty(receipt, RECEIPT_EPOCH, { value: receiptEpoch });
    if (receiptEpoch < 0 || receipt?.complete === false) scheduleReconciliation();
    return result as T;
  } finally {
    if (!settled) { settleChatRead(epoch); cancelChatsReads(); applyUnreadNotificationReceipt(null); scheduleReconciliation(); }
  }
}

export function reconcileNotificationRead(receipt: NotificationReadReceipt): void {
  void queryClient.cancelQueries({ queryKey: queryKeys.notifications });
  void queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
  if (isCurrentReceipt(receipt)) applyUnreadNotificationReceipt(receipt.unreadNotificationCount);
  if (!isCurrentReceipt(receipt) || receipt.unreadNotificationCount === null) scheduleReconciliation();
}

/** Each request does bounded owner work; every continuation keeps the same frozen token. */
async function pollReceipt<T>(request: () => Promise<T>, receipt: (value: T) => NotificationReadReceipt | null,
  active: () => boolean): Promise<T> {
  const until = Date.now() + 30_000;
  let value: T;
  do {
    if (!active()) throw new DOMException('Read operation no longer active', 'AbortError');
    value = await request();
    const notifications = receipt(value);
    // A legacy grouped row can gain members. A partial result must wait for an
    // explicit retry instead of repeatedly reading that group in the background.
    if (notifications === null || notifications.complete || notifications.readMode === 'VIEWER_ROWS' || Date.now() >= until) return value;
    await new Promise(resolve => setTimeout(resolve, 150));
  } while (active());
  return value;
}

/** Only a successfully rendered, visible latest window may acknowledge its server boundary. */
export function useChatReadAcknowledgment(owner: string | null, conversationId: string | null, token: string | null,
  eligible: boolean, refreshBoundary?: () => void | Promise<void>) {
  const selectionRef = useRef({ owner, conversationId, expired: false, waitingForFresh: false,
    retryAfterToken: null as string | null, viewerRetryToken: null as string | null, viewerRetryRequested: false });
  if (selectionRef.current.owner !== owner || selectionRef.current.conversationId !== conversationId) {
    selectionRef.current = { owner, conversationId, expired: false, waitingForFresh: false,
      retryAfterToken: null, viewerRetryToken: null, viewerRetryRequested: false };
  }
  const selection = selectionRef.current;
  const [failedSelection, setFailedSelection] = useState<typeof selection | null>(null);
  const [retry, setRetry] = useState(0);
  const current = useRef({ owner, conversationId, token, refreshBoundary });
  current.current = { owner, conversationId, token, refreshBoundary };
  const finished = useRef(new Set<string>());
  const inflight = useRef(new Set<string>());
  useEffect(() => {
    if (!owner || !conversationId || !token || !eligible) return;
    // A 410 retires this selection's boundary. Polling/visibility refreshes may
    // load new messages, but only an explicit retry may capture a new read.
    if (selection.expired) {
      if (!selection.waitingForFresh || token === selection.retryAfterToken) return;
      selection.expired = false;
      selection.waitingForFresh = false;
      selection.viewerRetryToken = null;
      selection.viewerRetryRequested = false;
    }
    if (selection.viewerRetryToken && !selection.viewerRetryRequested) return;
    const attemptToken = selection.viewerRetryToken ?? token;
    let disposed = false;
    let frame: number | undefined;
    const acknowledge = () => {
      if (document.visibilityState !== 'visible' || disposed || selection.expired
        || (selection.viewerRetryToken && !selection.viewerRetryRequested)
        || finished.current.has(attemptToken) || inflight.current.has(attemptToken)) return;
      frame = requestAnimationFrame(() => {
        if (disposed || selection.expired || document.visibilityState !== 'visible'
          || (selection.viewerRetryToken && !selection.viewerRetryRequested)
          || finished.current.has(attemptToken) || inflight.current.has(attemptToken)) return;
        selection.viewerRetryRequested = false;
        inflight.current.add(attemptToken);
        setFailedSelection(null);
        void pollReceipt(() => postChatRead<ConversationReadReceipt>({ action: 'conversation-acknowledge', conversationId, readThroughToken: attemptToken }),
          receipt => receipt.notifications, () => !disposed && current.current.owner === owner && currentChatOwner() === owner)
          .then(receipt => {
            if (receipt.conversationId !== conversationId || !receipt.readState
              || (receipt.notifications !== null && !receipt.notifications?.operationId)) throw new Error('Invalid read receipt');
            const complete = receipt.notifications === null || receipt.notifications.complete;
            if (complete) {
              finished.current.add(attemptToken);
              while (finished.current.size > 100) finished.current.delete(finished.current.values().next().value as string);
            }
            const active = current.current;
            if (currentChatOwner() !== owner) return;
            // A streaming/visibility transition can dispose this effect while
            // its mutation still finishes. Retain a partial row selection for
            // this conversation so returning to eligibility cannot retry it.
            if (!complete && receipt.notifications?.readMode === 'VIEWER_ROWS' && selectionRef.current === selection) {
              selection.viewerRetryToken = attemptToken;
              selection.viewerRetryRequested = false;
              setFailedSelection(selection);
            } else if (complete && receipt.notifications?.readMode === 'VIEWER_ROWS'
              && selectionRef.current === selection && selection.viewerRetryToken === attemptToken) {
              selection.viewerRetryToken = null;
              selection.viewerRetryRequested = false;
              setFailedSelection(null);
            }
            if (disposed || active.owner !== owner || active.conversationId !== conversationId || active.token !== token) {
              scheduleReconciliation();
              return;
            }
            if (attemptToken === token && isCurrentReceipt(receipt.notifications ?? receipt)) applyChatsAcknowledgment(owner, conversationId, receipt.readState);
            else invalidateChats();
            if (receipt.notifications) reconcileNotificationRead(receipt.notifications);
            setFailedSelection(complete ? null : selection);
          }).catch((error: unknown) => {
            if (disposed || selectionRef.current !== selection || current.current.token !== token || currentChatOwner() !== owner) return;
            if (error instanceof ReadSnapshotExpiredError) {
              selection.expired = true;
              selection.waitingForFresh = false;
            }
            setFailedSelection(selection);
          })
          .finally(() => { inflight.current.delete(attemptToken); });
      });
    };
    acknowledge();
    document.addEventListener('visibilitychange', acknowledge);
    return () => { disposed = true; if (frame !== undefined) cancelAnimationFrame(frame); document.removeEventListener('visibilitychange', acknowledge); };
  }, [owner, conversationId, token, eligible, retry, selection]);
  return { error: failedSelection === selection, retry: () => {
    if (selectionRef.current !== selection) return;
    if (selection.viewerRetryToken && !selection.expired) selection.viewerRetryRequested = true;
    if (selection.expired) {
      const refresh = current.current.refreshBoundary;
      if (!refresh) return;
      selection.waitingForFresh = true;
      selection.retryAfterToken = current.current.token;
      // reload schedules a fetch; its promise is not evidence that the new
      // window has rendered. The effect above waits for its new, eligible token.
      void Promise.resolve().then(refresh).catch(() => {
        if (selectionRef.current !== selection) return;
        selection.waitingForFresh = false;
        setFailedSelection(selection);
      });
    }
    setRetry(value => value + 1);
  } };
}

export async function acknowledgeAllNotifications(snapshotToken: string, active: () => boolean = () => true): Promise<NotificationReadReceipt> {
  const receipt = await pollReceipt(() => postChatRead<NotificationReadReceipt>({ action: 'notifications-read-acknowledge', snapshotToken }),
    value => value, active);
  if (!active()) throw new DOMException('Read operation no longer active', 'AbortError');
  if (!receipt.operationId || typeof receipt.complete !== 'boolean') throw new Error('Invalid notification read receipt');
  invalidateChats();
  reconcileNotificationRead(receipt);
  return receipt;
}
